import { useEffect, useRef } from 'react';
import './TurnstileWidget.css';

/**
 * MVP-471. The Cloudflare Turnstile widget, as one small component with one job: render the
 * challenge and hand the resulting token up.
 *
 * Used on two surfaces that share nothing else — the guest join gate (`RequestPage`, where every
 * guest meets it once a night) and the operator login box (`LoginDialog`, only from the third
 * attempt after two failures) — which is why it lives as its own component rather than inline in
 * either. Neither one decides anything: the token this produces proves nothing until the server
 * checks it against Cloudflare's siteverify endpoint.
 *
 * It renders no copy of its own, deliberately. The surrounding screen already says why a challenge
 * is on screen, in the reader's own language; the widget itself is Cloudflare's and picks up the
 * browser's language on its own.
 */

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: { sitekey: string; callback: (token: string) => void; 'error-callback'?: () => void; 'expired-callback'?: () => void }
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * One load per page, shared by every mount. The guest page mounts this once, but the passcode gate
 * and a reconnect can unmount and remount it, and appending the script a second time re-executes
 * Cloudflare's bundle for no benefit. A rejected promise is retried by the next mount, since it is
 * only cached while pending or resolved.
 */
let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('turnstile script failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onUnavailable,
}: {
  siteKey: string;
  /** Fires every time Cloudflare produces a token — including after an expiry, since the widget
   * re-challenges itself and the caller needs the fresh one. */
  onToken: (token: string) => void;
  /** The script was blocked or failed to load. A guest on a network that blocks Cloudflare (or an
   * aggressive content blocker) would otherwise see an empty box and no way forward, so the caller
   * is told rather than left showing nothing. */
  onUnavailable?: () => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  // The callbacks are read through a ref so a parent re-render (which re-creates the closures)
  // never re-renders the widget itself — Turnstile re-issues a challenge when it is re-rendered,
  // and a challenge that restarts under the reader is a challenge nobody can finish.
  const handlersRef = useRef({ onToken, onUnavailable });
  handlersRef.current = { onToken, onUnavailable };

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => handlersRef.current.onToken(token),
          'error-callback': () => handlersRef.current.onUnavailable?.(),
        });
      })
      .catch(() => {
        if (!cancelled) handlersRef.current.onUnavailable?.();
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey]);

  return <div className="turnstile-widget" ref={containerRef} />;
}
